// Registration Store

import { cloneDeep, camelCase, upperFirst, find, filter } from 'lodash'
import { getField, updateField } from 'vuex-map-fields'

const registrationData = {
  agbs: false,
  subscriberNumber: null,
  email: null,
  gift: null,
  payment: null,
  subscription: null,
  personalData: {
    topics: [],
    newsletter_subscription: false,
    birthday: null,
  },
  deliveryAddress: {
    title: '',
    gender: 0,
    firstname: '',
    lastname: '',
    address: '',
    house_number: '',
    zip: '',
    country: '',
    city: '',
    address_details: '',
    address_2: '',
    email: '',
  },
  invoiceAddress: {
    title: '',
    gender: 0,
    firstname: '',
    lastname: '',
    address: '',
    house_number: '',
    zip: '',
    country: '',
    city: '',
    email: '',
    password: '',
    address_details: '',
    address_2: '',
    separate_delivery_address: false,
  },
}

const state = {
  activeStep: 1,
  addressDataOptions: {},
  cart: {},
  order: {},
  countries: [],
  giftOptions: [],
  registrationData: cloneDeep(registrationData),
  subscriptionOptions: [],
  paymentOptions: [],
  // pending states will be set as key in pending
  pending: {},
  errors: [],
}

const getters = {
  getField,
}

const actions = {
  saveEmailAddress({ commit, state }) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'saveEmailAddress' })
      this.api.items
        .createItem('carts.mine.invoice_address', state.registrationData.invoiceAddress)
        .then(({ data }) => {
          if (data.errors) {
            commit('REGISTER_ACCOUNT_FAIL')
            reject(data.errors)
            return
          }

          commit('REGISTER_ACCOUNT_SUCCESS')
          resolve(data)
        })
        .catch(errors => {
          commit('REGISTER_ACCOUNT_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'saveEmailAddress' })
        })
    })
  },
  registerAccount({ commit }) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'registerAccount' })
      const data = { agb: state.registrationData.agbs, ...state.registrationData.invoiceAddress }
      this.api.items
        .createItem('users.new', data)
        .then(({ data }) => {
          if (data.errors) {
            commit('REGISTER_ACCOUNT_FAIL')
            reject(data.errors)
            return
          }
          commit('REGISTER_ACCOUNT_SUCCESS')
          resolve(data)
        })
        .catch(errors => {
          commit('REGISTER_ACCOUNT_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'registerAccount' })
        })
    })
  },
  editAccount({ commit }, id) {
    return new Promise((resolve, reject) => {
      const data = { ...state.registrationData.invoiceAddress, ...state.registrationData.personalData }
      delete data.gender
      commit('SET_PENDING', { pending: true, type: 'editAccount' })
      this.api.items
        .editItem(`users.${id}`, data)
        .then(({ data }) => {
          if (data.errors) {
            commit('EDIT_ACCOUNT_FAIL')
            reject(data.errors)
            return
          }
          commit('EDIT_ACCOUNT_SUCCESS')
          resolve(data)
        })
        .catch(errors => {
          commit('EDIT_ACCOUNT_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'editAccount' })
        })
    })
  },
  validateSubscriberNumber({ commit, state }) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'validateSubscriberNumber' })
      this.api.items
        .createItem(`registration.${state.registrationData.subscriberNumber}`)
        .then(({ data }) => {
          if (data.errors) {
            commit('VALIDATE_SUBSCRIBER_NUMBER_FAIL')
            reject(data.errors)
            return
          }

          commit('VALIDATE_SUBSCRIBER_NUMBER_SUCCESS')
          resolve(data)
        })
        .catch(errors => {
          commit('VALIDATE_SUBSCRIBER_NUMBER_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'validateSubscriberNumber' })
        })
    })
  },
  getCountries({ commit }) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'countries' })
      this.api.items
        .getItems('carts.mine.countries')
        .then(({ data }) => {
          if (data.errors) {
            commit('SET_COUNTRIES_FAIL')
            reject(data.errors)
            return
          }
          commit('SET_COUNTRIES_SUCCESS', data)
          resolve(data)
        })
        .catch(errors => {
          commit('SET_COUNTRIES_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'countries' })
        })
    })
  },
  getSubscriptionOptions({ commit }) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'subscriptionOptions' })
      this.api.items
        .getItems('shop.subscription_options')
        .then(({ data }) => {
          if (data.errors) {
            commit('SET_SUBSCRIPTION_OPTIONS_FAIL')
            reject(data.errors)
            return
          }
          commit('SET_SUBSCRIPTION_OPTIONS_SUCCESS', data)
          resolve(data)
        })
        .catch(errors => {
          commit('SET_SUBSCRIPTION_OPTIONS_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'subscriptionOptions' })
        })
    })
  },
  getGiftOptions({ commit }) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'giftOptions' })
      this.api.items
        .getItems('shop.gifts')
        .then(({ data }) => {
          if (data.errors) {
            commit('SET_GIFT_OPTIONS_FAIL')
            reject(data.errors)
            return
          }

          commit('SET_GIFT_OPTIONS_SUCCESS', data)
          resolve(data)
        })
        .catch(errors => {
          commit('SET_GIFT_OPTIONS_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'giftOptions' })
        })
    })
  },
  getPaymentOptions({ commit }) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'paymentOptions' })
      this.api.items
        .getItems('payments')
        .then(({ data }) => {
          if (data.errors) {
            commit('SET_PAYMENT_OPTIONS_FAIL')
            reject(data.errors)
            return
          }

          commit('SET_PAYMENT_OPTIONS_SUCCESS', data)
          resolve(data)
        })
        .catch(errors => {
          commit('SET_PAYMENT_OPTIONS_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'paymentOptions' })
        })
    })
  },
  getAddressDataOptions({ commit }) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'addressDataOptions' })
      this.api.items
        .getItems('carts.mine')
        .then(({ data }) => {
          if (data.errors) {
            commit('SET_ADDRESS_DATA_OPTIONS_FAIL')
            reject(data.errors)
            return
          }

          commit('SET_ADDRESS_DATA_OPTIONS_SUCCESS', data.form_options)
          resolve(data.form_options)
        })
        .catch(errors => {
          commit('SET_ADDRESS_DATA_OPTIONS_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'addressDataOptions' })
        })
    })
  },
  saveAddress({ commit }, { addressType, address }) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: `save${upperFirst(camelCase(addressType))}` })
      this.api.items
        .createItem(`carts.mine.${addressType}`, address)
        .then(({ data }) => {
          if (data.errors) {
            commit('SAVE_ADDRESS_FAIL')
            reject(data.errors)
            return
          }

          commit('SAVE_ADDRESS_SUCCESS')
          resolve(data)
        })
        .catch(errors => {
          commit('SAVE_ADDRESS_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: `save${upperFirst(camelCase(addressType))}` })
        })
    })
  },
  getPersonalDataTopics({ commit }) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'personalDataTopics' })
      this.api.items
        .getItems('registration.topics')
        .then(({ data }) => {
          if (data.errors) {
            commit('SET_PERSONAL_DATA_TOPICS_FAIL')
            reject(data.errors)
            return
          }

          commit('SET_PERSONAL_DATA_TOPICS_SUCCESS', data)
          resolve(data)
        })
        .catch(errors => {
          commit('SET_PERSONAL_DATA_TOPICS_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'personalDataTopics' })
        })
    })
  },
  savePersonalData({ commit, state }) {
    return new Promise((resolve, reject) => {
      const data = { ...state.registrationData.invoiceAddress, ...state.registrationData.personalData }
      commit('SET_PENDING', { pending: true, type: 'savePersonalData' })
      this.api.items
        .createItem('registration.user_data', data)
        .then(({ data }) => {
          if (data.errors) {
            commit('SAVE_PERSONAL_DATA_FAIL')
            reject(data.errors)
            return
          }

          commit('SAVE_PERSONAL_DATA_SUCCESS')
          resolve(data)
        })
        .catch(errors => {
          commit('SAVE_PERSONAL_DATA_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'savePersonalData' })
        })
    })
  },
  setPaymentOption({ commit }, payment_data) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'setPaymentOption' })
      this.api.items
        .createItem('carts.mine.payment', payment_data)
        .then(({ data }) => {
          if (data.errors) {
            commit('SET_PAYMENT_OPTION_FAIL')
            reject(data.errors)
            return
          }
          commit('SET_PAYMENT_OPTION_SUCCESS')
          resolve(data)
        })
        .catch(errors => {
          commit('SET_PAYMENT_OPTION_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'setPaymentOption' })
        })
    })
  },
  getCart({ commit }) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'getCart' })
      this.api.items
        .getItems('carts.mine')
        .then(({ data }) => {
          if (data.errors) {
            commit('SET_CART_FAIL')
            reject(data.errors)
            return
          }
          commit('SET_CART_SUCCESS', data)
          resolve(data)
        })
        .catch(errors => {
          commit('SET_CART_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'getCart' })
        })
    })
  },
  getOrder({ commit }, id) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'getOrder' })
      this.api.items
        .getItems(`orders.${id}`)
        .then(({ data }) => {
          if (data.errors) {
            commit('SET_ORDER_FAIL')
            reject(data.errors)
            return
          }
          commit('SET_ORDER_SUCCESS', data)
          resolve(data)
        })
        .catch(errors => {
          commit('SET_ORDER_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'getOrder' })
        })
    })
  },
  resetRegistration({ commit }) {
    commit('RESET_REGISTRATION')
  },
  clearErrors({ commit }) {
    commit('CLEAR_ERRORS')
  },
  updatePersonalDataTopic({ commit }, args) {
    commit('UPDATE_PERSONAL_DATA_TOPIC', args)
  },
  clearOrder({ commit }) {
    commit('CLEAR_ORDER')
  },
}

const mutations = {
  SET_PENDING: (state, { pending, type = 'pending' }) => {
    // to create the reactivity, so that Vue respects the change
    state.pending = { ...state.pending, [type]: pending }
  },
  UPDATE_PERSONAL_DATA_TOPIC: (state, { topic_id, option_id, args }) => {
    const topic = find(state.registrationData.personalData.topics, { id: topic_id })
    const option = find(topic.options, { id: option_id })
    option.user_selected = args
  },
  SET_COUNTRIES_SUCCESS(state, data) {
    state.countries = filter(data, country => country.code !== '')
  },
  SET_SUBSCRIPTION_OPTIONS_SUCCESS(state, data) {
    state.subscriptionOptions = data
  },
  SET_GIFT_OPTIONS_SUCCESS(state, data) {
    state.giftOptions = data
  },
  SET_PAYMENT_OPTIONS_SUCCESS(state, { services }) {
    state.paymentOptions = services
  },
  SET_CART_SUCCESS(state, { cart }) {
    state.cart = cart
  },
  SET_ORDER_SUCCESS(state, { cart }) {
    state.order = cart
  },
  SET_ADDRESS_DATA_OPTIONS_SUCCESS(state, { gender_options, invoice_address_country_options, delivery_address_country_options }) {
    const options = state.addressDataOptions
    options.gender_options = gender_options.splice(0, 1) && gender_options
    options.delivery_address_country_options = delivery_address_country_options.splice(0, 1) && delivery_address_country_options
    options.invoice_address_country_options = invoice_address_country_options.splice(0, 1) && invoice_address_country_options
  },
  SET_PERSONAL_DATA_TOPICS_SUCCESS(state, topics) {
    state.registrationData.personalData.topics = topics
  },
  RESET_REGISTRATION: state => {
    state.activeStep = 1
    state.addressDataOptions = {}
    state.giftOptions = []
    state.paymentOptions = []
    state.registrationData = cloneDeep(registrationData)
    state.subscriptionOptions = []
    state.errors = []
  },
  CLEAR_ERRORS: state => {
    state.errors = []
  },
  CLEAR_ORDER: state => {
    state.order = {}
  },
  updateField,
}

/*
 * This commits are only for the vuex history (visible in the vue devtools tab 2)
 * if you need to store data with any history commit, then remove the commit from
 * the history_commits and create a new commit in the mutation block.
 */
const history_commits = [
  'EDIT_ACCOUNT_SUCCESS',
  'EDIT_ACCOUNT_FAIL',
  'REGISTER_ACCOUNT_SUCCESS',
  'REGISTER_ACCOUNT_FAIL',
  'CHANGE_EMAIL_ADDRESS_SUCCESS',
  'CHANGE_EMAIL_ADDRESS_FAIL',
  'VALIDATE_SUBSCRIBER_NUMBER_SUCCESS',
  'VALIDATE_SUBSCRIBER_NUMBER_FAIL',
  'SAVE_ADDRESS_SUCCESS',
  'SAVE_ADDRESS_FAIL',
  'SAVE_PERSONAL_DATA_FAIL',
  'SAVE_PERSONAL_DATA_SUCCESS',
  'SET_COUNTRIES_FAIL',
  'SET_PAYMENT_OPTION_SUCCESS',
  'SET_PAYMENT_OPTION_FAIL',
  'SET_SUBSCRIPTION_OPTIONS_FAIL',
  'SET_GIFT_OPTIONS_FAIL',
  'SET_PAYMENT_OPTIONS_FAIL',
  'SET_CART_FAIL',
  'SET_ORDER_FAIL',
  'SET_ADDRESS_DATA_OPTIONS_FAIL',
  'SET_PERSONAL_DATA_TOPICS_FAIL',
]

history_commits.forEach(commit => {
  mutations[commit] = _ => true
})

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
